import _ from 'lodash';
import axios from 'axios';
import { store } from '../../app/store';
import { VITE_APP_BASE_ENDPOINT } from '../constant';

// We do it this way because Jest unit test can't find import.meta.env
let apiEndpoint = VITE_APP_BASE_ENDPOINT;
// NOTE: Changed the VITE_APP_BASE_ENDPOINT to this: http://localhost:9035/api",
// with different port number depend on your apache configuration.
// They are the same, but we use the localhost instead, so we don't get the
// cross-site Set-Cookies error.

/**
 * Make sure that we don't accidentally stringify json object twice.
 * @param payload
 * @returns
 */
function safeJSONStringify(payload: string | object) {
  if (typeof payload == 'string') {
    return payload;
  } else return JSON.stringify(payload);
}

function addGloballyRequestData(payload: any) {
  if (typeof payload === 'string') {
    payload = JSON.parse(payload);
  }
  Object.assign(payload, {
    width: Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
    isInApp: store.getState().app.isInApp,
    locale: store.getState().app.langCode,
    appid: store.getState().app.appid,
    device: store.getState().app.device,
  });
  return payload;
}

function applyUrlModification(url: string) {
  const urlObj = new URL(url);
  let alreadyHasParam = urlObj.searchParams.toString() !== '';

  if (import.meta.env.NODE_ENV === 'development') {
    if (alreadyHasParam) {
      url += '&XDEBUG_SESSION_START=PHPSTORM';
    } else {
      url += '?XDEBUG_SESSION_START=PHPSTORM';
    }
  }
  return url;
}

const getConfig = async (langCode?: string) => {
  let url = `${apiEndpoint}/config`;
  if (langCode) {
    url += '?langCode=' + langCode;
  }
  const response = await axios.get(url);
  return response.data;
};

const getLocalizations = async (langCode?: string) => {
  let url = `${apiEndpoint}/localizations`;
  if (langCode) {
    url += '?langCode=' + langCode;
  }
  const response = await axios.get(url);
  return response.data;
};

const getCountries = async (langCode?: string) => {
  let url = `${apiEndpoint}/countries`;
  if (langCode) {
    url += '?langCode=' + langCode;
  }
  const response = await axios.get(url);
  return response.data;
};

const getAirports = async (langCode?: string) => {
  let url = `${apiEndpoint}/airports`;
  if (langCode) {
    url += '?langCode=' + langCode;
  }
  const response = await axios.get(url);
  return response.data;
};

const getFqtv = async (langCode?: string) => {
  let url = `${apiEndpoint}/fqtv`;
  if (langCode) {
    url += '?langCode=' + langCode;
  }
  const response = await axios.get(url);
  return response.data;
};

const getLanguages = async (langCode?: string) => {
  let url = `${apiEndpoint}/languages`;
  if (langCode) {
    url += '?langCode=' + langCode;
  }
  const response = await axios.get(url);
  return response.data;
};

const getCarriers = async (langCode?: string) => {
  let url = `${apiEndpoint}/carriers`;
  if (langCode) {
    url += '?langCode=' + langCode;
  }
  const response = await axios.get(url);
  return response.data;
};

const getRelevantErrorToLog = (err: any) => {
  if (err.response) {
    if (err.response.data) {
      return err.response.data;
    }
    return err.response;
  }
  if (err.request) {
    return err.request;
  }
  return err;
};

const prepareLogPayload = (logPayload: any, formatted: boolean) => {
  let formattedPayload = logPayload;
  if (!formatted) {
    formattedPayload = {
      type: 'error',
      message: safeJSONStringify(getRelevantErrorToLog(logPayload)),
    };
  }
  return safeJSONStringify(formattedPayload);
};

const setLogging = async (logPayload: any, formatted = false) => {
  let url = `${apiEndpoint}/logging`;
  url = applyUrlModification(url);
  const response = await axios.post(url, prepareLogPayload(logPayload, formatted));
  return response;
};

/**
 * Same as setLogging, but does not wait for response nor aborted
 * if the site navigated. Use case: log before navigate to 3rd-party websites.
 * @param logPayload string | object
 * @param formatted boolean
 */
const setLoggingBeacon = (logPayload: any, formatted: boolean = false) => {
  let url = `${apiEndpoint}/logging`;
  url = applyUrlModification(url);
  navigator.sendBeacon(url, prepareLogPayload(logPayload, formatted));
};

const postVerifyCaptchaV2 = async (captchaToken: string) => {
  let payload = { captchaToken: captchaToken };
  let url = `${apiEndpoint}/react-utility/verify-captcha-v2`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), { withCredentials: true });
  return response;
};

const postVerifyCaptchaV3 = async (captchaToken: string, captchaAction: string) => {
  let payload = { captchaToken: captchaToken, captchaAction: captchaAction };
  let url = `${apiEndpoint}/react-utility/verify-captcha-v3`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), { withCredentials: true });
  return response;
};

// SOAP

/**
 * Go to confirmation from selectflight, without submit not checked in
 * passengers / Go to ck_confirmation using the Confirmation button.
 * @returns
 */
const getConfirmation = async (payload: any) => {
  let url = `${apiEndpoint}/get-confirmation`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), { withCredentials: true });
  return response;
};

/**
 * Submit the data of not selected, not checked in passengers.
 * Go to ck_confirmation using the Continue button.
 * @param payload
 * @returns
 */
const doCheckin = async (payload: any) => {
  let url = `${apiEndpoint}/confirmation`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

/**
 * Submit retrieve form. ck_retrieve to ck_selectflight
 * @param payload
 * @returns
 */
const setCheckinDetails = async (payload: any) => {
  let url = `${apiEndpoint}/retrieve`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, safeJSONStringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const reRetrieve = async (payload: any) => {
  let url = `${apiEndpoint}/soap/reRetrieve`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, safeJSONStringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const getBoardingPass = async (payload: any) => {
  let url = `${apiEndpoint}/get-boarding-pass`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, safeJSONStringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const getDocument = async (payload: any) => {
  let url = `${apiEndpoint}/get-document`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, safeJSONStringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const getAddPax = async (payload: any) => {
  let url = `${apiEndpoint}/get-add-pax`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const getUpdateIdentityDocument = async (payload: any) => {
  let url = `${apiEndpoint}/get-update-identity-document`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const setUpdateIdentityDocument = async (payload: any) => {
  let url = `${apiEndpoint}/set-update-identity-document`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const getApisData = async (payload: any) => {
  let url = `${apiEndpoint}/get-apis`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), { withCredentials: true });
  return response;
};

const getSafeTravel = async (payload: any) => {
  let url = `${apiEndpoint}/get-safe-travel`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const acceptScheduleChange = async (payload: any) => {
  let url = `${apiEndpoint}/accept-schedule-change`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const skipSafeTravel = async (payload: any) => {
  let url = `${apiEndpoint}/skip-safe-travel`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const finalizePayment = async () => {
  let url = `${apiEndpoint}/finalize-payment`;
  url = applyUrlModification(url);
  const response = await axios.get(url, { withCredentials: true });
  return response;
};

const finalizeSafetravel = async (payload: any) => {
  let url = `${apiEndpoint}/finalize-safe-travel`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, newPayload, { withCredentials: true });
  return response;
};

const postAPISData = async (payload: any) => {
  let url = `${apiEndpoint}/set-apis`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, safeJSONStringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const getCheckoutData = async (payload: any) => {
  let url = `${apiEndpoint}/checkout`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const postCheckoutData = async (payload: any) => {
  let url = `${apiEndpoint}/do-checkout`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const postConfirmationData = async (payload: any) => {
  let url = `${apiEndpoint}/set-confirmation`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));

  const response = await axios.post(url, JSON.stringify(newPayload), { withCredentials: true });
  return response;
};

const getFlightSeatmap = async (payload: any) => {
  let url = `${apiEndpoint}/seatmap`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const getContact = async (payload: any) => {
  let url = `${apiEndpoint}/get-contact`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const setContact = async (payload: any) => {
  let url = `${apiEndpoint}/update-contact`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const postSeatmapData = async (payload: any) => {
  let url = `${apiEndpoint}/change-seats`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const postAlterChargeableSeats = async (payload: any) => {
  let url = `${apiEndpoint}/alter-chargeable-seats`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const validateFqtv = async (payload: any) => {
  let url = `${apiEndpoint}/validate-fqtv`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
  });
  return response;
};

const downloadFlightIcs = async (payload: any) => {
  let url = `${apiEndpoint}/download-calendar`;
  url = applyUrlModification(url);
  let newPayload = addGloballyRequestData(_.cloneDeep(payload));
  const response = await axios.post(url, JSON.stringify(newPayload), {
    withCredentials: true,
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response;
};

export {
  getConfig,
  getLocalizations,
  getCountries,
  getAirports,
  getCarriers,
  getFqtv,
  getLanguages,
  setLogging,
  getConfirmation,
  getAddPax,
  setCheckinDetails,
  getBoardingPass,
  getDocument,
  doCheckin,
  getApisData,
  getSafeTravel,
  acceptScheduleChange,
  skipSafeTravel,
  finalizePayment,
  finalizeSafetravel,
  postAPISData,
  getCheckoutData,
  postConfirmationData,
  getFlightSeatmap,
  postSeatmapData,
  postAlterChargeableSeats,
  postCheckoutData,
  setLoggingBeacon,
  postVerifyCaptchaV2,
  postVerifyCaptchaV3,
  getUpdateIdentityDocument,
  setUpdateIdentityDocument,
  getContact,
  setContact,
  validateFqtv,
  downloadFlightIcs,
  reRetrieve,
};
